import React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import { useDrop } from "src/core/drops/hooks";
import { IStore } from "src/core/shared/store";
import { useOrder } from "src/core/orders/hooks";
import useList from "src/core/lists/hooks/useList";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";

import { ProductPricing } from "./ProductPricing";
import {
  IProductMaybeWithSubstitution,
  getCurrentListQuantity,
  getCurrentQuantity
} from "../../utils";
import { ProductUnavailableTag } from "../ProductUnavailableTag";
import { ProductQuantityControls } from "./ProductQuantityControls";
import { IProduct } from "../../api/products.api";
import useProductIsOrderable from "../../hooks/useProductIsOrderable";
import { useProductIsAllowedSubs } from "../../hooks/useProductIsAllowedSubs";

interface ProductOrderingControlsProps {
  product: IProductMaybeWithSubstitution;
  context: "edit" | "view";
  CreateOrderButton?: (product: IProduct) => React.ReactNode;
  orderable?: boolean;
  lineMessage?: string;
}

export const ProductOrderingControls: React.FC<ProductOrderingControlsProps> = ({
  product, context, CreateOrderButton, lineMessage
}) => {
  const { installedModules } = useMappedState((store: IStore) => ({ installedModules: (store.global.settings && store.global.settings.installedModules) || [] }));
  const checkUserPermission = useCheckUserPermission();
  const [ { drop } ] = useDrop();
  const [ { currentOrder } ] = useOrder();
  const [ { currentList } ] = useList();
  const allowedSubs = useProductIsAllowedSubs(product);
  const unavailable = allowedSubs && product.substitution?.message || null;
  const { orderable, unavailableMessage } = useProductIsOrderable(product, unavailable);
  const unavailableLineMessage = lineMessage || unavailableMessage;

  const currentQuantity = React.useMemo(() => {
    if (currentList.data) {
      return getCurrentListQuantity(product.id, currentList.data);
    } else {
      return getCurrentQuantity(product.id, currentOrder.data);
    }
  }, [
    currentList.data,
    currentOrder.data,
    product
  ]);

  return (
    <>
      <ProductPricing
        product={product.substitution?.replacementProduct || product}
        quantity={currentQuantity || 1}
        drop={drop}
        installedModules={installedModules}
      />

      {!orderable && unavailableLineMessage && <ProductUnavailableTag reason={unavailableLineMessage} />}

      {checkUserPermission("create-order") && orderable && context === "view" && (
        <ButtonWrapper>
          {CreateOrderButton && orderable ? CreateOrderButton(product) : null}
        </ButtonWrapper>
      )}

      {context === "edit" && (
        <ProductQuantityControls
          product={product}
          orderable={orderable}
        />
      )}
    </>
  );
};

const ButtonWrapper = styled(Grid.Box)`
  ${() => css`
    button {
      min-width: 0; 
    }
  `}
`;